import React, { useEffect, useRef, useState } from 'react';

import { dealTokens } from './designTokens';

const C = {
  accent: dealTokens.color.primary,
  ink: dealTokens.color.text,
  border: dealTokens.color.border,
  muted: dealTokens.color.textMuted,
  white: dealTokens.color.surface,
  danger: dealTokens.color.danger,
};

const BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: 'inherit',
  fontWeight: dealTokens.font.weightMedium,
  borderRadius: dealTokens.radius.md,
  whiteSpace: 'nowrap',
  transition: `opacity ${dealTokens.motion.normal}, background ${dealTokens.motion.normal}, border-color ${dealTokens.motion.normal}`,
  cursor: 'pointer',
  lineHeight: 1,
};

const VARIANTS = {
  primary: {
    background: C.accent,
    color: C.white,
    border: 'none',
    disabledBg: C.border,
    disabledColor: C.muted,
  },
  ghost: {
    background: 'transparent',
    color: C.ink,
    border: `1px solid ${C.border}`,
    disabledBg: 'transparent',
    disabledColor: C.muted,
  },
  danger: {
    background: 'transparent',
    color: C.danger,
    border: `1px solid ${C.danger}`,
    disabledBg: 'transparent',
    disabledColor: C.muted,
  },
};

// `md` is the only size with an explicit `minHeight` below — without one, a button's
// height falls out of padding + font-size + the base `lineHeight: 1` alone, which
// renders noticeably shorter than every `md` button around it (e.g. PlanTab.js's
// "Aggiungi esercizio" `sm` button next to `md` ones in the same toolbar). Giving
// `sm` its own minHeight keeps it visibly smaller than `md` (36px) while still a
// deliberate, consistent height rather than "however tall the content happens to be".
const SIZES = {
  sm: { padding: '5px 12px', iconPadding: '5px', fontSize: 12, gap: 4, iconSize: 14, minHeight: dealTokens.control.sm },
  md: { padding: '9px 16px', iconPadding: '9px', fontSize: 13, gap: 6, iconSize: 16, minHeight: dealTokens.control.md },
  lg: { padding: '11px 20px', iconPadding: '11px', fontSize: 14, gap: 7, iconSize: 18 },
};

// Inline chevron — no MUI import needed in a shared component.
const Chevron = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    style={{ transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}
  >
    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ActionButton = ({ children, variant = 'primary', size = 'md', icon, disabled = false, onClick, type = 'button', tooltip, style, ...rest }) => {
  const v = VARIANTS[variant] ?? VARIANTS.primary;
  const s = SIZES[size] ?? SIZES.md;
  const iconOnly = !children && !!icon;
  const tooltipLabel = tooltip || (iconOnly ? rest['aria-label'] : null);

  const resolvedStyle = {
    ...BASE,
    gap: s.gap,
    padding: iconOnly ? s.iconPadding : s.padding,
    justifyContent: 'center',
    fontSize: s.fontSize,
    minHeight: s.minHeight,
    background: disabled ? v.disabledBg : v.background,
    color: disabled ? v.disabledColor : v.color,
    border: disabled && variant !== 'ghost' ? `1px solid ${C.border}` : v.border,
    opacity: disabled ? 0.65 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    position: 'relative',
    ...style,
  };

  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={tooltipLabel || undefined}
      style={resolvedStyle}
      onMouseEnter={e => {
        if (!disabled) e.currentTarget.style.opacity = '0.82';
      }}
      onMouseLeave={e => {
        if (!disabled) e.currentTarget.style.opacity = '1';
      }}
      {...rest}
    >
      {/* The caller's own icon style is spread LAST so an explicitly passed `color`
          wins over the per-variant default. It used to be the other way round, which
          made the default unconditional: a caller-set colour was accepted and then
          silently overwritten. GeneralInformation.js's social chips are the case that
          exposed it — they colour each network icon by whether that field is filled
          in (muted when empty, brand when set) and every icon came out brand-coloured,
          so the "nothing entered here yet" signal the legacy page had was lost. */}
      {icon &&
        React.cloneElement(icon, {
          style: {
            fontSize: s.iconSize,
            flexShrink: 0,
            color: variant === 'ghost' ? C.accent : 'currentColor',
            ...(icon.props?.style ?? {}),
          },
        })}
      {children}
    </button>
  );
};

// Canonical system action. New forms and toolbars should import SystemButton;
// ActionButton remains as the backwards-compatible name.
export const SystemButton = ActionButton;

export const SystemIconButton = ({ icon, label, variant = 'ghost', disabled = false, onClick, type = 'button' }) => (
  <ActionButton
    type={type}
    variant={variant}
    size="md"
    icon={icon}
    aria-label={label}
    tooltip={label}
    disabled={disabled}
    onClick={onClick}
    style={{ width: 36, height: 36, minWidth: 36, minHeight: 36, padding: 0 }}
  />
);

/**
 * Smart action group for PageToolbar's actions slot.
 *
 * 1 action  → single primary button (icon + label)
 * 2+ actions → split button: primary action on the left, chevron on the right
 *              opens a dropdown with the remaining actions.
 *
 * Each action: { label, icon, onClick, variant?, disabled?, danger?, key? }
 */
export const ActionGroup = ({ actions = [] }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = e => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (actions.length === 0) return null;

  const [primary, ...secondary] = actions;

  // Single action — plain button, nothing extra.
  if (secondary.length === 0) {
    return (
      <ActionButton variant={primary.variant || 'primary'} size="md" icon={primary.icon} onClick={primary.onClick} disabled={primary.disabled}>
        {primary.label}
      </ActionButton>
    );
  }

  // Split button — primary action left, chevron opens dropdown with the rest.
  const btnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    background: C.accent,
    color: C.white,
    border: 'none',
    fontFamily: 'inherit',
    fontWeight: 500,
    fontSize: 13,
    lineHeight: 1,
    height: 36,
    boxSizing: 'border-box',
    cursor: 'pointer',
    transition: 'opacity .15s',
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
      {/* Pill wrapper — shared border-radius, overflow clips the inner divider flush */}
      <div style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden' }}>
        {/* Primary action */}
        <button
          type="button"
          disabled={primary.disabled}
          onClick={primary.onClick}
          style={{ ...btnBase, gap: 6, padding: '9px 14px', opacity: primary.disabled ? 0.55 : 1 }}
          onMouseEnter={e => {
            if (!primary.disabled) e.currentTarget.style.opacity = '0.82';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.opacity = primary.disabled ? '0.55' : '1';
          }}
        >
          {primary.icon && React.cloneElement(primary.icon, { style: { fontSize: 16, flexShrink: 0 } })}
          {primary.label}
        </button>

        {/* Thin white divider between the two halves */}
        <div style={{ width: 1, background: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />

        {/* Chevron trigger */}
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{ ...btnBase, padding: '9px 9px', justifyContent: 'center' }}
          onMouseEnter={e => {
            e.currentTarget.style.opacity = '0.82';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          <Chevron open={open} />
        </button>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 50,
            minWidth: 190,
            background: '#fff',
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
            padding: 4,
          }}
        >
          {secondary.map((action, i) => (
            <button
              key={action.key || i}
              type="button"
              disabled={action.disabled}
              onClick={() => {
                setOpen(false);
                action.onClick();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                border: 'none',
                background: 'transparent',
                borderRadius: 7,
                padding: '8px 10px',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'inherit',
                color: action.danger ? C.danger : C.ink,
                cursor: action.disabled ? 'not-allowed' : 'pointer',
                opacity: action.disabled ? 0.45 : 1,
                textAlign: 'left',
              }}
              onMouseEnter={e => {
                if (!action.disabled) e.currentTarget.style.background = '#F9FAFB';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {action.icon &&
                React.cloneElement(action.icon, {
                  style: { fontSize: 16, flexShrink: 0, color: action.danger ? C.danger : C.muted },
                })}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionButton;
